import React, { useRef, useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';

const AcademicYearForm = ({
  formData,
  setFormData,
  loading,
  editAcademicYearId,
  handleSubmitAcademicYear,
  resetForm,
  handleInputChange,
  formErrors = {},
  onFieldBlur,
  onSemesterFieldBlur,
  getInputClasses,
  topLevelError
}) => {
  const nameInputRef = useRef(null);
  const formRef = useRef(null);
  const [showErrors, setShowErrors] = useState(false);
  const [localErrors, setLocalErrors] = useState({});
  const errorToastShown = useRef(false);
  const hasShownTopLevelError = useRef(false);

  // Focus on name input when form is shown
  useEffect(() => {
    if (nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, []);

  // Handle top-level errors only once when they appear
  useEffect(() => {
    if (topLevelError && !hasShownTopLevelError.current) {
      toast.error(topLevelError, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      hasShownTopLevelError.current = true;
    }
    
    if (!topLevelError) {
      hasShownTopLevelError.current = false;
    }
  }, [topLevelError]);

  // Store form errors locally but don't auto-show them
  useEffect(() => {
    setLocalErrors(formErrors || {});
  }, [formErrors]);

  // Prevent scroll wheel from changing number inputs
  useEffect(() => {
    const handleWheel = (e) => {
      if (e.target.type === 'number' && e.target.closest('.no-wheel')) {
        e.preventDefault();
      }
    };

    document.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      document.removeEventListener('wheel', handleWheel);
    };
  }, []);

  // Enhanced handleSubmit with validation display
  const handleSubmitWithToast = async (e) => {
    e.preventDefault();
    
    // Show all errors on submit
    setShowErrors(true);
    
    // Check if there are any errors
    const hasErrors = Object.keys(localErrors || {}).some(key => {
      if (typeof localErrors[key] === 'string') {
        return !!localErrors[key];
      }
      if (localErrors[key] && typeof localErrors[key] === 'object') {
        return Object.values(localErrors[key]).some(val => !!val);
      }
      return false;
    });

    if (hasErrors) {
      // Find first error field to focus
      const errorKeys = Object.keys(localErrors || {});
      if (errorKeys.length > 0) {
        const firstErrorKey = errorKeys[0];
        let target = null;
        
        // Try to find the input element
        if (firstErrorKey.includes('SemesterDates')) {
          // Handle semester date errors
          const semesterType = firstErrorKey.includes('odd') ? 'odd' : 'even';
          const fieldType = localErrors[firstErrorKey].startDate ? 'startDate' : 'endDate';
          target = document.querySelector(`[name="${semesterType}Semester.${fieldType}"]`);
        } else {
          target = document.querySelector(`[name="${firstErrorKey}"]`);
        }
        
        if (target) {
          // Small delay to ensure error messages are rendered
          setTimeout(() => {
            target.focus({ preventScroll: true });
          }, 50);
        }
      }
      
      // Show single toast about errors
      if (!errorToastShown.current) {
        toast.error('Please fix the errors in the form before submitting.', {
          position: "top-right",
          autoClose: 5000,
        });
        errorToastShown.current = true;
        setTimeout(() => {
          errorToastShown.current = false;
        }, 5000);
      }
      return;
    }

    try {
      await handleSubmitAcademicYear(e);
      setShowErrors(false);
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error(error?.message || 'Failed to save academic year.', {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  // Custom handleInputChange with debounced blur for better UX
  const handleCustomInputChange = useCallback((e) => {
    handleInputChange(e);
    
    // Clear the showErrors flag when user starts editing
    setShowErrors(false);
  }, [handleInputChange]);

  // Custom blur handler that doesn't immediately show errors
  const handleCustomBlur = useCallback((fieldName) => {
    if (onFieldBlur) {
      onFieldBlur(fieldName);
    }
  }, [onFieldBlur]);

  // Custom semester blur handler
  const handleCustomSemesterBlur = useCallback((semesterType, fieldName) => {
    if (onSemesterFieldBlur) {
      onSemesterFieldBlur(semesterType, fieldName);
    }
  }, [onSemesterFieldBlur]);

  // Custom number input handler for semester count
  const handleNumberInputChange = useCallback((e) => {
    const { name, value } = e.target;
    const numValue = parseInt(value, 10);
    
    // Only allow positive numbers
    if (isNaN(numValue) || numValue < 1) {
      handleInputChange({ target: { name, value: '' } });
    } else {
      handleInputChange({ target: { name, value: numValue.toString() } });
    }
  }, [handleInputChange]);

  // Helper to get error message for a field (only shown after submit attempt)
  const getErrorMessage = (fieldName) => {
    if (!showErrors) return null;
    if (typeof localErrors[fieldName] === 'string') {
      return localErrors[fieldName];
    }
    return null;
  };

  // Helper to get semester error message
  const getSemesterErrorMessage = (semesterType, fieldName) => {
    if (!showErrors) return null;
    if (localErrors[`${semesterType}SemesterDates`]?.[fieldName]) {
      return localErrors[`${semesterType}SemesterDates`][fieldName];
    }
    return null;
  };

  // NEW: Common label suggestions for quick selection
  const labelSuggestions = ['UG', 'PG', 'Engineering', 'Medical', 'Arts', 'Science', 'Commerce', 'Management'];

  return (
    <div ref={formRef} className="bg-white rounded-2xl shadow-xl border border-gray-200">
      <div className="p-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {editAcademicYearId ? 'Edit Academic Year' : 'Create New Academic Year'}
            </h2>
            <p className="text-gray-600 mt-2">
              {editAcademicYearId ? 'Update the academic year details' : 'Define a new academic period'}
            </p>
          </div>
          <button
            onClick={resetForm}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-100"
            type="button"
            aria-label="Close form"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <form onSubmit={handleSubmitWithToast} className="space-y-8" noValidate>
          {/* Basic Information */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Academic Year Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-3">Academic Year Name *</label>
              <input
                type="text"
                name="name"
                ref={nameInputRef}
                value={formData.name}
                onChange={handleCustomInputChange}
                onBlur={() => handleCustomBlur('name')}
                required
                className={getInputClasses ? getInputClasses('name') : `w-full px-4 py-3 border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 ${
                  getErrorMessage('name') 
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
                    : 'border-gray-300 focus:border-blue-500'
                }`}
                placeholder="e.g. 2023-2024"
                aria-describedby={getErrorMessage('name') ? "name-error" : undefined}
              />
              {getErrorMessage('name') && (
                <p id="name-error" className="mt-2 text-sm text-red-600 flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  {getErrorMessage('name')}
                </p>
              )}
            </div>

            {/* NEW: Label Field */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-3">
                Label *
                <span className="ml-2 text-xs font-normal text-gray-500">
                  (Helps distinguish similar academic years)
                </span>
              </label>
              <input
                type="text"
                name="label"
                value={formData.label || ''}
                onChange={handleCustomInputChange}
                onBlur={() => handleCustomBlur('label')}
                required
                maxLength={50}
                className={getInputClasses ? getInputClasses('label') : `w-full px-4 py-3 border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 ${
                  getErrorMessage('label') 
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
                    : 'border-gray-300 focus:border-blue-500'
                }`}
                placeholder="e.g. UG, PG, Engineering, Others"
                aria-describedby={getErrorMessage('label') ? "label-error" : "label-hint"}
                autoComplete="off"
              />
              {getErrorMessage('label') ? (
                <p id="label-error" className="mt-2 text-sm text-red-600 flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  {getErrorMessage('label')}
                </p>
              ) : (
                <div id="label-hint" className="mt-2">
                  <p className="text-xs text-gray-500 mb-2">Quick select or enter your own:</p>
                  <div className="flex flex-wrap gap-2">
                    {labelSuggestions.concat('Others').map((suggestion) => (
                      <button
                        key={suggestion}
                        type="button"
                        onClick={() => {
                          if (suggestion === 'Others') {
                            // Focus input for free entry
                            const labelInput = document.querySelector('[name="label"]');
                            if (labelInput) {
                              labelInput.focus();
                            }
                            handleCustomInputChange({ target: { name: 'label', value: '' } });
                          } else {
                            handleCustomInputChange({ target: { name: 'label', value: suggestion } });
                          }
                        }}
                        className={`px-3 py-1 text-xs rounded-full border transition-all duration-200 ${
                          formData.label === suggestion
                            ? 'bg-blue-100 border-blue-500 text-blue-700'
                            : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100 hover:border-gray-400'
                        }`}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    You can type any custom label in the box above.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Semester-based Toggle */}
          <div className="flex items-center space-x-4 bg-gray-50 p-4 rounded-xl">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isSemesterBased"
                name="isSemesterBased"
                checked={formData.isSemesterBased}
                onChange={handleCustomInputChange}
                className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="isSemesterBased" className="ml-3 text-sm font-medium text-gray-900">
                Semester-based System
              </label>
            </div>
            <div className="w-px h-6 bg-gray-300"></div>
            <span className="text-xs text-gray-500 flex-1">
              Enable to define semesters within this academic year
            </span>
          </div>

          {/* NEW: Display Name Preview */}
          {formData.name && formData.label && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
              <p className="text-sm text-gray-600 mb-1">Display Name Preview:</p>
              <p className="text-lg font-semibold text-blue-900">
                {formData.name} ({formData.label})
              </p>
            </div>
          )}

          {/* Academic Period */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Academic Period</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Start Date *</label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleCustomInputChange}
                  onBlur={() => handleCustomBlur('startDate')}
                  required
                  className={getInputClasses ? getInputClasses('startDate') : `w-full px-4 py-3 border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 ${
                    getErrorMessage('startDate') 
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
                      : 'border-gray-300 focus:border-blue-500'
                  }`}
                  aria-describedby={getErrorMessage('startDate') ? "startDate-error" : undefined}
                />
                {getErrorMessage('startDate') && (
                  <p id="startDate-error" className="mt-2 text-sm text-red-600 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    {getErrorMessage('startDate')}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">End Date *</label>
                <input
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleCustomInputChange}
                  onBlur={() => handleCustomBlur('endDate')}
                  required
                  className={getInputClasses ? getInputClasses('endDate') : `w-full px-4 py-3 border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 ${
                    getErrorMessage('endDate') 
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
                      : 'border-gray-300 focus:border-blue-500'
                  }`}
                  aria-describedby={getErrorMessage('endDate') ? "endDate-error" : undefined}
                />
                {getErrorMessage('endDate') && (
                  <p id="endDate-error" className="mt-2 text-sm text-red-600 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    {getErrorMessage('endDate')}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Semester Configuration */}
          {formData.isSemesterBased && (
            <div className="space-y-8">
              <div className="border-t border-gray-200 pt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Semester Configuration</h3>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">Number of Semesters *</label>
                  <div className="no-wheel">
                    <input
                      type="number"
                      name="numberOfSemesters"
                      min={1}
                      max={10}
                      value={formData.numberOfSemesters}
                      onChange={handleNumberInputChange}
                      onBlur={() => handleCustomBlur('numberOfSemesters')}
                      required={formData.isSemesterBased}
                      className={getInputClasses ? getInputClasses('numberOfSemesters') : `w-full px-4 py-3 border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 ${
                        getErrorMessage('numberOfSemesters') 
                          ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 focus:border-blue-500'
                      }`}
                      placeholder="Enter number of semesters"
                      aria-describedby={getErrorMessage('numberOfSemesters') ? "numberOfSemesters-error" : undefined}
                      onWheel={(e) => e.preventDefault()}
                    />
                  </div>
                  {getErrorMessage('numberOfSemesters') && (
                    <p id="numberOfSemesters-error" className="mt-2 text-sm text-red-600 flex items-center gap-1">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      {getErrorMessage('numberOfSemesters')}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Odd Semester */}
                  <div className="bg-blue-50 rounded-2xl p-6 border border-blue-200">
                    <h4 className="text-lg font-semibold text-blue-900 mb-4 flex items-center gap-2">
                      <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                      Odd Semester Period
                    </h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-blue-700 mb-2">Start Date *</label>
                        <input
                          type="date"
                          name="oddSemester.startDate"
                          value={formData.oddSemesterDates.startDate}
                          onChange={handleCustomInputChange}
                          onBlur={() => handleCustomSemesterBlur('odd', 'startDate')}
                          required={formData.isSemesterBased}
                          className={getInputClasses ? getInputClasses('oddSemester.startDate', true) : `w-full px-4 py-3 border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 bg-white ${
                            getSemesterErrorMessage('odd', 'startDate') 
                              ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
                              : 'border-blue-200 focus:border-blue-500'
                          }`}
                          aria-describedby={getSemesterErrorMessage('odd', 'startDate') ? "oddSemesterStartDate-error" : undefined}
                        />
                        {getSemesterErrorMessage('odd', 'startDate') && (
                          <p id="oddSemesterStartDate-error" className="mt-2 text-sm text-red-600 flex items-center gap-1">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                            {getSemesterErrorMessage('odd', 'startDate')}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-blue-700 mb-2">End Date *</label>
                        <input
                          type="date"
                          name="oddSemester.endDate"
                          value={formData.oddSemesterDates.endDate}
                          onChange={handleCustomInputChange}
                          onBlur={() => handleCustomSemesterBlur('odd', 'endDate')}
                          required={formData.isSemesterBased}
                          className={getInputClasses ? getInputClasses('oddSemester.endDate', true) : `w-full px-4 py-3 border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 bg-white ${
                            getSemesterErrorMessage('odd', 'endDate') 
                              ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
                              : 'border-blue-200 focus:border-blue-500'
                          }`}
                          aria-describedby={getSemesterErrorMessage('odd', 'endDate') ? "oddSemesterEndDate-error" : undefined}
                        />
                        {getSemesterErrorMessage('odd', 'endDate') && (
                          <p id="oddSemesterEndDate-error" className="mt-2 text-sm text-red-600 flex items-center gap-1">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                            {getSemesterErrorMessage('odd', 'endDate')}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Even Semester */}
                  <div className="bg-purple-50 rounded-2xl p-6 border border-purple-200">
                    <h4 className="text-lg font-semibold text-purple-900 mb-4 flex items-center gap-2">
                      <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                      Even Semester Period
                    </h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-purple-700 mb-2">Start Date *</label>
                        <input
                          type="date"
                          name="evenSemester.startDate"
                          value={formData.evenSemesterDates.startDate}
                          onChange={handleCustomInputChange}
                          onBlur={() => handleCustomSemesterBlur('even', 'startDate')}
                          required={formData.isSemesterBased && formData.numberOfSemesters >= 2}
                          disabled={formData.numberOfSemesters < 2}
                          className={getInputClasses ? getInputClasses('evenSemester.startDate', true) : `w-full px-4 py-3 border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all duration-200 bg-white ${
                            getSemesterErrorMessage('even', 'startDate') 
                              ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
                              : formData.numberOfSemesters < 2 
                                ? 'border-gray-300 bg-gray-50 text-gray-400' 
                                : 'border-purple-200 focus:border-purple-500'
                          }`}
                          aria-describedby={getSemesterErrorMessage('even', 'startDate') ? "evenSemesterStartDate-error" : undefined}
                        />
                        {getSemesterErrorMessage('even', 'startDate') && (
                          <p id="evenSemesterStartDate-error" className="mt-2 text-sm text-red-600 flex items-center gap-1">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                            {getSemesterErrorMessage('even', 'startDate')}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-purple-700 mb-2">End Date *</label>
                        <input
                          type="date"
                          name="evenSemester.endDate"
                          value={formData.evenSemesterDates.endDate}
                          onChange={handleCustomInputChange}
                          onBlur={() => handleCustomSemesterBlur('even', 'endDate')}
                          required={formData.isSemesterBased && formData.numberOfSemesters >= 2}
                          disabled={formData.numberOfSemesters < 2}
                          className={getInputClasses ? getInputClasses('evenSemester.endDate', true) : `w-full px-4 py-3 border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all duration-200 bg-white ${
                            getSemesterErrorMessage('even', 'endDate') 
                              ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
                              : formData.numberOfSemesters < 2 
                                ? 'border-gray-300 bg-gray-50 text-gray-400' 
                                : 'border-purple-200 focus:border-purple-500'
                          }`}
                          aria-describedby={getSemesterErrorMessage('even', 'endDate') ? "evenSemesterEndDate-error" : undefined}
                        />
                        {getSemesterErrorMessage('even', 'endDate') && (
                          <p id="evenSemesterEndDate-error" className="mt-2 text-sm text-red-600 flex items-center gap-1">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                            {getSemesterErrorMessage('even', 'endDate')}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Non-Semester-based Term Configuration */}
          {!formData.isSemesterBased && (
            <div className="space-y-8">
              <div className="border-t border-gray-200 pt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Term Configuration</h3>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">Number of Terms *</label>
                  <div className="no-wheel">
                    <input
                      type="number"
                      name="numberOfTerms"
                      min={1}
                      max={20}
                      value={formData.numberOfTerms || ''}
                      onChange={handleNumberInputChange}
                      onBlur={() => handleCustomBlur('numberOfTerms')}
                      required={!formData.isSemesterBased}
                      className={getInputClasses ? getInputClasses('numberOfTerms') : `w-full px-4 py-3 border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 ${
                        getErrorMessage('numberOfTerms') 
                          ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 focus:border-blue-500'
                      }`}
                      placeholder="Enter number of terms"
                      aria-describedby={getErrorMessage('numberOfTerms') ? "numberOfTerms-error" : undefined}
                      onWheel={(e) => e.preventDefault()}
                    />
                  </div>
                  {getErrorMessage('numberOfTerms') && (
                    <p id="numberOfTerms-error" className="mt-2 text-sm text-red-600 flex items-center gap-1">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      {getErrorMessage('numberOfTerms')}
                    </p>
                  )}
                </div>
                {/* Preview generated term labels */}
                {formData.numberOfTerms > 0 && (
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200 mt-4">
                    <p className="text-sm text-gray-600 mb-1">Term Labels Preview:</p>
                    <ul className="list-disc pl-6 text-blue-900">
                      {Array.from({ length: formData.numberOfTerms }, (_, idx) => (
                        <li key={idx} className="mb-1">Term {idx + 1}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {/* Editable term labels */}
                {formData.numberOfTerms > 0 && (
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200 mt-4">
                    <p className="text-sm text-gray-600 mb-1">Enter Term Labels:</p>
                    <ul className="space-y-2">
                      {Array.from({ length: formData.numberOfTerms }, (_, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <span className="text-blue-900 font-medium">Term {idx + 1}:</span>
                          <input
                            type="text"
                            name={`termLabels[${idx}]`}
                            value={formData.termLabels && formData.termLabels[idx] ? formData.termLabels[idx] : ''}
                            onChange={e => {
                              const newLabels = formData.termLabels ? [...formData.termLabels] : [];
                              newLabels[idx] = e.target.value;
                              setFormData(prev => ({ ...prev, termLabels: newLabels }));
                            }}
                            className="w-48 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 border-blue-200"
                            placeholder={`Term ${idx + 1}`}
                          />
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200">
            <button
              type="submit"
              disabled={loading}
              className={`flex-1 flex items-center justify-center gap-3 px-6 py-4 rounded-xl text-white font-semibold shadow-lg transition-all duration-200 ${
                loading 
                  ? 'bg-blue-400 cursor-not-allowed' 
                  : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 transform hover:-translate-y-0.5'
              }`}
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {editAcademicYearId ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {editAcademicYearId ? 'Update Academic Year' : 'Create Academic Year'}
                </>
              )}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="flex-1 px-6 py-4 bg-white text-gray-700 border border-gray-300 rounded-xl hover:bg-gray-50 transition-all duration-200 font-semibold"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AcademicYearForm;